import React, { useState } from 'react';
import { contactData } from '../data/mock';
import { MapPin, Mail, Phone, Building2, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission (will be connected to backend later)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
    
    // Reset success message after 5 seconds
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-14">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            {contactData.title}
          </h2>
          <div className="w-24 h-1.5 bg-[#DA8347] mx-auto rounded-full" />
        </div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-8">Informações de Contacto</h3>
              
              <div className="space-y-6">
                {/* Institution */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#DA8347] to-[#c46f35] rounded-xl flex items-center justify-center shadow-md">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Instituição</p>
                    <p className="font-semibold text-gray-900 text-lg">{contactData.institution}</p>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#DA8347] to-[#c46f35] rounded-xl flex items-center justify-center shadow-md">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Morada</p>
                    <p className="font-semibold text-gray-900 text-lg">{contactData.address}</p>
                    <p className="text-gray-600 mt-1">({contactData.additionalLocation})</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#DA8347] to-[#c46f35] rounded-xl flex items-center justify-center shadow-md">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <a 
                      href={`mailto:${contactData.email}`}
                      className="font-semibold text-[#DA8347] hover:underline text-lg"
                    >
                      {contactData.email}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#DA8347] to-[#c46f35] rounded-xl flex items-center justify-center shadow-md">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Telefone</p>
                    <a 
                      href={`tel:${contactData.phone.replace(/\s/g, '')}`}
                      className="font-semibold text-[#DA8347] hover:underline text-lg"
                    >
                      {contactData.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-10 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Envie-nos uma mensagem</h3>
            
            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <CheckCircle className="w-20 h-20 text-green-500 mb-4" />
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Mensagem enviada!</h4>
                <p className="text-gray-600 text-lg">Entraremos em contacto brevemente.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="nome" className="text-base font-semibold">Nome *</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="O seu nome completo"
                    className="bg-white h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base font-semibold">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="exemplo@email.com"
                    className="bg-white h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefone" className="text-base font-semibold">Telefone</Label>
                  <Input
                    id="telefone"
                    name="telefone"
                    type="tel"
                    value={formData.telefone}
                    onChange={handleChange}
                    placeholder="912 345 678"
                    className="bg-white h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mensagem" className="text-base font-semibold">Mensagem *</Label>
                  <Textarea
                    id="mensagem"
                    name="mensagem"
                    required
                    value={formData.mensagem}
                    onChange={handleChange}
                    placeholder="Escreva a sua mensagem..."
                    rows={4}
                    className="bg-white resize-none text-base"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#DA8347] hover:bg-[#c46f35] text-white py-6 text-lg font-bold transition-all duration-300 disabled:opacity-50 rounded-xl shadow-lg hover:shadow-xl"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      A enviar...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send size={22} />
                      Enviar pedido de contacto
                    </span>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
